import { AuthProvider, ProtectRoute } from 'context/user'
import { ImpersonationProvider } from 'context/impersonation'
import { SubscriptionUsageProvider } from 'context/subscription-usage'
import { GlobalStyles } from 'global'
import { useDarkMode } from 'hooks'
import { useMemo } from 'react'
import {
    BrowserRouter as Router,
    Outlet,
    Route,
    Routes,
    Navigate,
} from 'react-router-dom'
import { ThemeProvider } from 'styled-components'
import { darkTheme, lightTheme } from 'theme'
import {
    LoginScreen,
    PhoneOnboardingScreen,
    RestaurantExcellenceLoginScreen,
    TemplateListScreen,
    DailyAuditTemplateListScreen,
    MatFinishTemplateListScreen,
    RawMaterialQcInspectionTemplateListScreen,
    PlatenAuditTemplateListScreen,
    DailyDefectReportTemplateListScreen,
    AddEditTemplateScreen,
    ReportScreen,
    CheckReportScreen,
    GroupListScreen,
    CapaListPage,
    CustomerListScreen,
    InspectionList,
    MissingInspectionLink,
    TaskListScreen,
    MonthlyRotationScreen,
    VerificationScreen,
    SetPasswordScreen,
    InspectionReportScreen,
    CreateTemplateWithAnotherTemplatesScreen,
    SignUpScreen,
    ForgotPasswordScreen,
    ProfileScreen,
    CombineTemplateReportScreen,
    AddEditCustomReportScreen,
    SettingsScreen,
    CapaScreen,
    NewTemplateLibrary,
    AddPublicEditTemplateScreen,
    TicketScreen,
    MagicTicketScreen,
    AuditTicketsListScreen,
    TicketsListScreen,
    TicketDigestScreen,
    CustomerGroupSelectionScreen,
    DashboardCustomerListScreen,
    TemplateSelectionScreen,
    HomeDashboardScreen,
    SuperAdminScreen,
    DueTemplatesScreen,
    CustomerDefectReportScreen,
    SupportTicketListScreen,
    InspectionPreviewScreen,
    InspectionReviewScreen,
    AssetListScreen,
    AssetTypeListScreen,
    AssetSearchScreen,
    AssetAuditLogScreen,
    ScheduleAssetInspectionScreen,
    AssetDetailScreen,
    AssetScanScreen,
    AssetLocationListScreen,
    CustomFieldManagerScreen,
    AssetTemplateLinkingScreen,
    AccountPricingScreen,
    LicenseCheckoutScreen,
    TemplateApprovalWorkflowScreen,
    TemplateReasonPresetsScreen,
    TemplateReviewFlowScreen,
    ReportApprovalsListScreen,
    ReportApprovalDetailScreen,
    ReportApprovalSignScreen,
    CrmScreen,
} from './screens'
import {
    ConsolidatedReportEmailListScreen,
    ConsolidatedReportEmailFormScreen,
    ConsolidatedReportUnsubscribeScreen,
} from './screens/consolidated-report-email'
import { ScheduleInspectionsScreen } from './screens/asset/schedule-inspections'
import { ViewActionPlanScreen } from 'screens/action-plan/view'
import { ResponseProvider } from 'context/response'
import { ScrollToTop } from 'layout/Scroll'
import { ReactNotifications } from 'react-notifications-component'
import { FirebaseService } from 'utility'
import {
    ENABLE_ACCOUNT_PLAN_PAGE,
    ENABLE_LICENSE_SHOPPING_CART,
} from 'utility/subscription-usage'
import { ApprovalFlowGate } from 'page-components/report-approval/ApprovalFlowGate'
import { CumulativeReport } from 'page-components/report/cumulative-report'
import { AuditorSummaryScreen } from 'page-components/report/auditor-summary'
import { ProcessHealthScreen } from 'page-components/report/process-health'
import { ActionPlanScreen } from 'page-components/action-plan'
import {
    CreateAITemplateScreen,
    ReviewAITemplateScreen,
} from 'screens/template/create-ai-template'
import { ClientSetupScreen } from 'screens/setup/client-setup'
import { TestNewUserScreen } from 'screens/setup/test-new-user'
import { NestedQuestionnaireScreen } from 'screens/setup/nested-questionnaire'
import { CustomerDefectListScreen } from 'screens/customer-defect/list'
import { NotificationRecipientsScreen } from 'screens/customer-defect/notification-recipients'
import { DailyScrapReworkTemplateListScreen } from 'screens/template/daily-scrap-rework-list'
import { HundredInspectionTemplateListScreen } from 'screens/template/hundred-inspection-list'
import { MacolaScrapCodesScreen } from 'screens/template/macola-scrap-codes'
import { TuftLockTemplateListScreen } from 'screens/template/tuft-lock-list'
import { SmallDailyAuditTemplateListScreen } from 'screens/template/small-daily-audit-list'
import {
    AutoTicketRulesManageScreen,
    AutoTicketRulesCreateScreen,
    AutoTicketRulesEditScreen,
} from 'screens/template/auto-ticket-rules'
import { MasterAnswersScreen } from 'screens/master-answers'
import { MenuSettingScreen } from 'screens/menu-setting'
import { PermissionsScreen } from 'screens/permissions'
import { InvoiceListScreen } from './screens/invoice/list';
import { InvoiceSearchScreen } from './screens/invoice/search';
import { CompletedInvoiceSearchScreen } from 'screens/invoice/completed';
import { InvoiceEditor } from './screens/invoice/invoiceEditor';
import { InvoiceViewScreen } from 'screens/invoice/view';
import { RequireTemplateCreatePermission } from 'components'

FirebaseService.init()

function App() {
    const [theme] = useDarkMode()
    const themeMode = useMemo(
        () => (theme === 'light' ? lightTheme : darkTheme),
        [theme],
    )

    return (
        <AuthProvider>
            <ImpersonationProvider>
            <SubscriptionUsageProvider>
            <ResponseProvider>
                <ThemeProvider theme={themeMode}>
                    <GlobalStyles />
                    <ReactNotifications />
                    <Router>
                        <ScrollToTop>
                            <Routes>
                                <Route
                                    path="/"
                                    exact
                                    replace
                                    element={<Navigate to="app/task" />}
                                />
                                <Route
                                    path="/complete-phone"
                                    exact
                                    element={<PhoneOnboardingScreen />}
                                />
                                <Route
                                    path="/sign-in"
                                    exact
                                    element={<LoginScreen />}
                                />
                                <Route
                                    path="/sign-in-franchising"
                                    exact
                                    element={
                                        <RestaurantExcellenceLoginScreen />
                                    }
                                />
                                <Route
                                    path="/sign-up"
                                    exact
                                    element={<SignUpScreen />}
                                />
                                <Route
                                    path="/forgot-password"
                                    exact
                                    element={<ForgotPasswordScreen />}
                                />
                                <Route
                                    path="/verification"
                                    exact
                                    element={<VerificationScreen />}
                                />
                                <Route
                                    path="/set-password"
                                    exact
                                    element={<SetPasswordScreen />}
                                />
                                <Route
                                    path="/public/inspection/:id"
                                    exact
                                    element={<InspectionList publicScreen />}
                                />
                                <Route
                                    path="/app/public/inspection/:id"
                                    exact
                                    element={<InspectionList publicScreen />}
                                />
                                <Route
                                    path="/app/public/ticket/:id"
                                    exact
                                    element={<ActionPlanScreen publicScreen />}
                                />
                                <Route
                                    path="/app/public/tickets/digest/:token"
                                    exact
                                    element={<TicketDigestScreen />}
                                />
                                <Route
                                    path="/app/public/tickets/:id"
                                    exact
                                    element={<MagicTicketScreen />}
                                />
                                <Route
                                    path="/public/tickets/magic/:token"
                                    exact
                                    element={<MagicTicketScreen />}
                                />
                                <Route
                                    path="/public/tickets/:id"
                                    exact
                                    element={<MagicTicketScreen />}
                                />
                                {/* e-mailed no-login approval link: open for every company (the token is checked by the server) */}
                                <Route
                                    path="/public/approval/:token"
                                    exact
                                    element={<ReportApprovalSignScreen />}
                                />
                                <Route
                                    path="/app/public/action-plan/:id"
                                    exact
                                    element={<ActionPlanScreen publicScreen />}
                                />
                                <Route
                                    path="/app/public/user/ActionPlan/GetById/:id/Signature"
                                    exact
                                    element={<ActionPlanScreen publicScreen />}
                                />
                                {/* <Route
                                    path="/app/templates/inspection/:id"
                                    exact
                                    element={
                                        <InspectionList publicScreen={false} />
                                    }
                                /> */}
                                <Route
                                    path="/public/capa"
                                    exact
                                    element={<CapaScreen />}
                                />
                                {/* Spec 2 §8: public unsubscribe confirmation (email footer link). Live API returns HTML; this route is the FE preview. */}
                                <Route
                                    path="/public/consolidated-report-unsubscribe"
                                    exact
                                    element={
                                        <ConsolidatedReportUnsubscribeScreen />
                                    }
                                />
                                <Route
                                    path="/ConsolidatedReportUnsubscribe"
                                    exact
                                    element={
                                        <ConsolidatedReportUnsubscribeScreen />
                                    }
                                />
                                <Route
                                    path="/forgot-password"
                                    exact
                                    element={<ForgotPasswordScreen />}
                                />
                                <Route
                                    path="app"
                                    element={
                                        <ProtectRoute>
                                            <Outlet />
                                        </ProtectRoute>
                                    }
                                >
                                    <Route
                                        path="profile"
                                        element={<ProfileScreen />}
                                    />
                                    <Route path="crm" element={<CrmScreen />} />
                                    <Route
                                        path="crm/:view"
                                        element={<CrmScreen />}
                                    />
                                    <Route
                                        path="home"
                                        element={<HomeDashboardScreen />}
                                    />
                                    <Route
                                        path="super-admin"
                                        element={<SuperAdminScreen />}
                                    />
                                    <Route
                                        path="account/plan"
                                        element={
                                            <Navigate
                                                to={
                                                    ENABLE_ACCOUNT_PLAN_PAGE
                                                        ? '/app/settings/account-plan'
                                                        : '/app/settings'
                                                }
                                                replace
                                            />
                                        }
                                    />
                                    <Route
                                        path="account/pricing"
                                        element={
                                            ENABLE_ACCOUNT_PLAN_PAGE ? (
                                                <AccountPricingScreen />
                                            ) : (
                                                <Navigate
                                                    to="/app/settings"
                                                    replace
                                                />
                                            )
                                        }
                                    />
                                    <Route
                                        path="account/checkout"
                                        element={
                                            ENABLE_LICENSE_SHOPPING_CART ? (
                                                <LicenseCheckoutScreen />
                                            ) : (
                                                <Navigate
                                                    to={
                                                        ENABLE_ACCOUNT_PLAN_PAGE
                                                            ? '/app/settings/account-plan'
                                                            : '/app/settings'
                                                    }
                                                    replace
                                                />
                                            )
                                        }
                                    />
                                    <Route path="settings" element={<Outlet />}>
                                        <Route
                                            index
                                            element={<SettingsScreen />}
                                        />
                                        <Route
                                            path=":tab"
                                            element={<SettingsScreen />}
                                        />
                                    </Route>
                                    <Route
                                        path="templates"
                                        exact
                                        element={<Outlet />}
                                    >
                                        <Route
                                            index
                                            element={<TemplateListScreen />}
                                        />
                                        <Route
                                            path="daily-audit"
                                            element={<DailyAuditTemplateListScreen />}
                                        />
                                        <Route
                                            path="small-daily-audit"
                                            element={<SmallDailyAuditTemplateListScreen />}
                                        />
                                        <Route
                                            path="mat-finish"
                                            element={<MatFinishTemplateListScreen />}
                                        />
                                        <Route
                                            path="raw-material-qc-inspection"
                                            element={<RawMaterialQcInspectionTemplateListScreen />}
                                        />
                                        <Route
                                            path="hundred-inspection"
                                            element={<HundredInspectionTemplateListScreen />}
                                        />
                                        <Route
                                            path="tuft-lock"
                                            element={<TuftLockTemplateListScreen />}
                                        />
                                        <Route
                                            path="platen-audit"
                                            element={<PlatenAuditTemplateListScreen />}
                                        />
                                        <Route
                                            path="daily-defect-report"
                                            element={<DailyDefectReportTemplateListScreen />}
                                        />
                                        <Route
                                            path="daily-scrap-rework"
                                            element={<DailyScrapReworkTemplateListScreen />}
                                        />
                                        <Route
                                            path="macola-scrap-codes"
                                            element={<MacolaScrapCodesScreen />}
                                        />
                                        <Route
                                            path="create"
                                            element={
                                                <RequireTemplateCreatePermission>
                                                    <AddEditTemplateScreen />
                                                </RequireTemplateCreatePermission>
                                            }
                                        />
                                        <Route
                                            path="create-with-templates"
                                            element={
                                                <RequireTemplateCreatePermission>
                                                    <CreateTemplateWithAnotherTemplatesScreen />
                                                </RequireTemplateCreatePermission>
                                            }
                                        />
                                        <Route
                                            path="create-ai-template"
                                            element={
                                                <RequireTemplateCreatePermission>
                                                    <Outlet />
                                                </RequireTemplateCreatePermission>
                                            }
                                        >
                                            <Route
                                                index
                                                element={
                                                    <CreateAITemplateScreen />
                                                }
                                            />
                                            <Route
                                                path="review"
                                                element={
                                                    <ReviewAITemplateScreen />
                                                }
                                            />
                                        </Route>
                                        <Route
                                            path="new-template-library"
                                            exact
                                            element={
                                                <RequireTemplateCreatePermission>
                                                    <Outlet />
                                                </RequireTemplateCreatePermission>
                                            }
                                        >
                                            <Route
                                                index
                                                element={<NewTemplateLibrary />}
                                            />
                                            <Route
                                                path="edit-public/:id"
                                                element={
                                                    <AddPublicEditTemplateScreen />
                                                }
                                            />
                                        </Route>
                                        <Route
                                            path="edit/:id"
                                            element={
                                                <RequireTemplateCreatePermission>
                                                    <AddEditTemplateScreen />
                                                </RequireTemplateCreatePermission>
                                            }
                                        />
                                        <Route
                                            path="inspection"
                                            element={<MissingInspectionLink />}
                                        />
                                        <Route
                                            path="inspection/:id"
                                            element={<InspectionList />}
                                        />
                                        <Route
                                            path="inspection/preview/:id"
                                            element={
                                                <InspectionPreviewScreen />
                                            }
                                        />
                                        <Route
                                            path="inspection/review/:id"
                                            element={<InspectionReviewScreen />}
                                        />
                                        <Route
                                            path="inspection/report/:id"
                                            element={<InspectionReportScreen />}
                                        />
                                        <Route
                                            path=":templateId/auto-rules"
                                            element={<Outlet />}
                                        >
                                            <Route
                                                index
                                                element={
                                                    <AutoTicketRulesManageScreen />
                                                }
                                            />
                                            <Route
                                                path="create"
                                                element={
                                                    <AutoTicketRulesCreateScreen />
                                                }
                                            />
                                            <Route
                                                path="edit/:ruleId"
                                                element={
                                                    <AutoTicketRulesEditScreen />
                                                }
                                            />
                                        </Route>
                                        <Route
                                            path=":templateId/approval-workflow"
                                            element={
                                                <ApprovalFlowGate redirectTo="/app/templates" adminOnly>
                                                    <TemplateApprovalWorkflowScreen />
                                                </ApprovalFlowGate>
                                            }
                                        />
                                        <Route
                                            path=":templateId/reason-presets"
                                            element={
                                                <TemplateReasonPresetsScreen />
                                            }
                                        />
                                        <Route
                                            path=":templateId/review-flow"
                                            element={
                                                <TemplateReviewFlowScreen />
                                            }
                                        />
                                    </Route>
                                    <Route
                                        path="customer-defect"
                                        exact
                                        element={<Outlet />}
                                    >
                                        <Route
                                            index
                                            element={
                                                <CustomerDefectListScreen />
                                            }
                                        />
                                        <Route
                                            path="create"
                                            element={
                                                <RequireTemplateCreatePermission redirectTo="/app/customer-defect">
                                                    <AddEditTemplateScreen />
                                                </RequireTemplateCreatePermission>
                                            }
                                        />
                                        <Route
                                            path="create-with-templates"
                                            element={
                                                <RequireTemplateCreatePermission redirectTo="/app/customer-defect">
                                                    <CreateTemplateWithAnotherTemplatesScreen />
                                                </RequireTemplateCreatePermission>
                                            }
                                        />
                                        <Route
                                            path="create-ai-template"
                                            element={
                                                <RequireTemplateCreatePermission redirectTo="/app/customer-defect">
                                                    <Outlet />
                                                </RequireTemplateCreatePermission>
                                            }
                                        >
                                            <Route
                                                index
                                                element={
                                                    <CreateAITemplateScreen />
                                                }
                                            />
                                            <Route
                                                path="review"
                                                element={
                                                    <ReviewAITemplateScreen />
                                                }
                                            />
                                        </Route>
                                        <Route
                                            path="new-template-library"
                                            exact
                                            element={
                                                <RequireTemplateCreatePermission redirectTo="/app/customer-defect">
                                                    <Outlet />
                                                </RequireTemplateCreatePermission>
                                            }
                                        >
                                            <Route
                                                index
                                                element={<NewTemplateLibrary />}
                                            />
                                            <Route
                                                path="edit-public/:id"
                                                element={
                                                    <AddPublicEditTemplateScreen />
                                                }
                                            />
                                        </Route>
                                        <Route
                                            path="edit/:id"
                                            element={
                                                <RequireTemplateCreatePermission redirectTo="/app/customer-defect">
                                                    <AddEditTemplateScreen />
                                                </RequireTemplateCreatePermission>
                                            }
                                        />
                                        <Route
                                            path="inspection/:id"
                                            element={<InspectionList />}
                                        />
                                        <Route
                                            path="inspection/preview/:id"
                                            element={
                                                <InspectionPreviewScreen />
                                            }
                                        />
                                        <Route
                                            path="inspection/review/:id"
                                            element={<InspectionReviewScreen />}
                                        />
                                        <Route
                                            path="inspection/report/:id"
                                            element={<InspectionReportScreen />}
                                        />
                                    </Route>
                                    <Route path="setup" element={<Outlet />}>
                                        <Route
                                            path="client-setup"
                                            element={<ClientSetupScreen />}
                                        />
                                        <Route
                                            path="test-new-user"
                                            element={<TestNewUserScreen />}
                                        />
                                        <Route
                                            path="nested-questionnaire"
                                            element={
                                                <NestedQuestionnaireScreen />
                                            }
                                        />
                                    </Route>
                                    <Route
                                        path="report"
                                        exact
                                        element={<Outlet />}
                                    >
                                        <Route
                                            index
                                            element={<ReportScreen />}
                                        />
                                        <Route
                                            path="create-custom-report/:id"
                                            element={
                                                <AddEditCustomReportScreen />
                                            }
                                        />
                                        <Route
                                            path="process-health"
                                            element={<ProcessHealthScreen />}
                                        />
                                    </Route>
                                    <Route
                                        path="invoice"
                                        exact
                                        element={<Outlet />}
                                    >
                                        <Route
                                            index
                                            element={<InvoiceListScreen />}
                                        />
                                        <Route
                                            path="search"
                                            element={<InvoiceSearchScreen />}
                                        />
                                        <Route
                                            path="completed"
                                            element={
                                                <CompletedInvoiceSearchScreen />
                                            }
                                        />
                                        <Route
                                            path="editor/:id"
                                            element={<InvoiceEditor />}
                                        />
                                        <Route
                                            path="view/:id"
                                            element={<InvoiceViewScreen />}
                                        />
                                    </Route>
                                    <Route
                                        path="checkin"
                                        exact
                                        element={<Outlet />}
                                    >
                                        <Route
                                            index
                                            element={<CheckReportScreen />}
                                        />
                                    </Route>
                                    <Route
                                        path="combine-template"
                                        exact
                                        element={<Outlet />}
                                    >
                                        <Route
                                            index
                                            element={
                                                <CombineTemplateReportScreen />
                                            }
                                        />
                                    </Route>
                                    <Route
                                        path="due-templates"
                                        exact
                                        element={<DueTemplatesScreen />}
                                    />
                                    <Route
                                        path="customer-defect-report"
                                        exact
                                        element={<CustomerDefectReportScreen />}
                                    />
                                    <Route
                                        path="notification-recipients"
                                        exact
                                        element={<NotificationRecipientsScreen />}
                                    />
                                    <Route
                                        path="customer"
                                        exact
                                        element={<Outlet />}
                                    >
                                        <Route
                                            index
                                            element={<CustomerListScreen />}
                                        />
                                    </Route>
                                    <Route
                                        path="support-tickets"
                                        exact
                                        element={<SupportTicketListScreen />}
                                    />
                                    <Route
                                        path="master-answers"
                                        exact
                                        element={<MasterAnswersScreen />}
                                    />
                                    <Route
                                        path="menu-settings"
                                        exact
                                        element={<MenuSettingScreen />}
                                    />
                                    <Route
                                        path="permission"
                                        exact
                                        element={<PermissionsScreen />}
                                    />
                                    <Route
                                        path="group"
                                        exact
                                        element={<Outlet />}
                                    >
                                        <Route
                                            index
                                            element={<GroupListScreen />}
                                        />
                                        <Route
                                            index
                                            path="capa-list"
                                            element={<CapaListPage />}
                                        />
                                    </Route>

                                    <Route
                                        path="consolidated-report-email"
                                        element={<Outlet />}
                                    >
                                        <Route
                                            index
                                            element={
                                                <ConsolidatedReportEmailListScreen />
                                            }
                                        />
                                        <Route
                                            path="new"
                                            element={
                                                <ConsolidatedReportEmailFormScreen />
                                            }
                                        />
                                        <Route
                                            path=":id/edit"
                                            element={
                                                <ConsolidatedReportEmailFormScreen />
                                            }
                                        />
                                    </Route>

                                    <Route
                                        path="report-approvals"
                                        element={<Outlet />}
                                    >
                                        <Route
                                            index
                                            element={
                                                <ApprovalFlowGate>
                                                    <ReportApprovalsListScreen />
                                                </ApprovalFlowGate>
                                            }
                                        />
                                        <Route
                                            path=":id"
                                            element={
                                                <ReportApprovalDetailScreen />
                                            }
                                        />
                                    </Route>

                                    <Route
                                        path="task"
                                        exact
                                        element={<Outlet />}
                                    >
                                        <Route
                                            index
                                            element={<TaskListScreen />}
                                        />
                                    </Route>
                                    <Route
                                        path="monthly-rotation"
                                        element={<MonthlyRotationScreen />}
                                    />
                                    <Route
                                        path="asset-management"
                                        element={<Outlet />}
                                    >
                                        <Route
                                            index
                                            element={<AssetListScreen />}
                                        />
                                        <Route
                                            path="types"
                                            element={<AssetTypeListScreen />}
                                        />
                                        <Route
                                            path="locations"
                                            element={
                                                <AssetLocationListScreen />
                                            }
                                        />
                                        <Route
                                            path="search"
                                            element={<AssetSearchScreen />}
                                        />
                                        <Route
                                            path="audit-log"
                                            element={<AssetAuditLogScreen />}
                                        />
                                        <Route
                                            path="schedule-inspection"
                                            element={
                                                <ScheduleAssetInspectionScreen />
                                            }
                                        />
                                        <Route
                                            path="scan"
                                            element={<AssetScanScreen />}
                                        />
                                        <Route
                                            path="custom-field-manager"
                                            element={<CustomFieldManagerScreen />}
                                        />
                                        <Route
                                            path="template-linking"
                                            element={<AssetTemplateLinkingScreen />}
                                        />
                                        <Route
                                            path="schedule-inspections"
                                            element={<ScheduleInspectionsScreen />}
                                        />
                                        <Route
                                            path=":id"
                                            element={<AssetDetailScreen />}
                                        />
                                    </Route>
                                    <Route
                                        path="action-plan"
                                        element={<Outlet />}
                                    >
                                        <Route
                                            index
                                            element={<ActionPlanScreen />}
                                        />
                                        <Route
                                            path="view/:questionId"
                                            element={<ViewActionPlanScreen />}
                                        />
                                    </Route>
                                    <Route
                                        path="cumulative-report"
                                        element={<CumulativeReport />}
                                    />
                                    <Route
                                        path="auditor-summary"
                                        element={<AuditorSummaryScreen />}
                                    />
                                    <Route
                                        path="tickets"
                                        element={<TicketsListScreen />}
                                    />
                                    <Route
                                        path="ticket/:id"
                                        element={<TicketScreen />}
                                    />
                                    <Route
                                        path="secure/tickets"
                                        element={<AuditTicketsListScreen />}
                                    />
                                    <Route
                                        path="secure/tickets/:id"
                                        element={<TicketScreen />}
                                    />
                                    <Route
                                        path="dashboard"
                                        element={<Outlet />}
                                    >
                                        <Route
                                            path="groups"
                                            element={
                                                <CustomerGroupSelectionScreen />
                                            }
                                        />
                                        <Route
                                            path="customers"
                                            element={
                                                <DashboardCustomerListScreen />
                                            }
                                        />
                                        <Route
                                            path="templates"
                                            element={
                                                <TemplateSelectionScreen />
                                            }
                                        />
                                    </Route>
                                </Route>
                            </Routes>
                        </ScrollToTop>
                    </Router>
                </ThemeProvider>
            </ResponseProvider>
            </SubscriptionUsageProvider>
            </ImpersonationProvider>
        </AuthProvider>
    )
}

export default App
